import { FaUser } from 'react-icons/fa';
import { FaEye } from 'react-icons/fa6';
import { TbCurrencyDollarCanadian } from 'react-icons/tb';
import { RetainAgreement } from '../../../types/retain';
import { showPicture } from '../../../utils/commonFunctions';
import RetainProgressBar from '../../HistoryComps/RetainComps/RetainProgressBar';
import AcademiaSectionWrapper from '../AcademiaSectionWrapper';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const RetainAgreementDetailsModal = ({
  title,
  retainHistory,
  handleModalClose,
}: {
  title: string;
  retainHistory: RetainAgreement;
  handleModalClose: () => void;
}) => {
  // destructuring the retain history object here
  const {
    associate_full_name,
    associate_profile_picture,
    client_full_name,
    client_profile_picture,
    start_date,
    end_date,
    monthly_fee,
    additional_hour_rate,
    hours_included,
    is_active,
    document,
    document_notes,
  } = retainHistory || {};

  // rendering retain history details modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[600px]'>
      <ModalTitle titleText={`${title} details`} />

      <div className='flex items-center justify-between'>
        <div>
          {associate_profile_picture ? (
            <div className='size-20 rounded-full overflow-hidden'>
              <img
                className='h-full w-full object-cover'
                src={showPicture(associate_profile_picture)}
                alt={`avatar of ${associate_full_name}`}
              />
            </div>
          ) : (
            <div className='flex items-center justify-center bg-base-light-gray size-20 rounded-full'>
              <FaUser className='size-12 text-gray-300' />
            </div>
          )}

          <p className='md:text-lg text-legal-dark-blue font-semibold mt-1'>
            {associate_full_name}
          </p>

          <span className='font-semibold text-base-light-blue italic text-sm md:text-base'>
            Associate
          </span>
        </div>

        <div>
          {client_profile_picture ? (
            <div className='size-20 rounded-full overflow-hidden ml-auto'>
              <img
                className='h-full w-full object-cover'
                src={showPicture(client_profile_picture)}
                alt={`avatar of ${client_full_name}`}
              />
            </div>
          ) : (
            <div className='flex items-center justify-center bg-base-light-gray size-20 rounded-full'>
              <FaUser className='size-12 text-gray-300' />
            </div>
          )}

          <p className='text-right md:text-lg text-legal-dark-blue font-semibold mt-1'>
            {client_full_name}
          </p>

          <span className='block font-semibold text-base-purple italic text-right text-sm md:text-base'>
            Client
          </span>
        </div>
      </div>

      <AcademiaSectionWrapper
        sectionTitle='Agreement Details'
        additionalClassName='!mt-7 !px-4 !pb-3'
      >
        <RetainProgressBar startingDate={start_date} endingDate={end_date} />

        <div className='border-t-2 border-dashed border-legal-dark-blue pt-1 mt-1'>
          <div className='flex items-start gap-1 text-legal-dark-blue italic'>
            <p className='capitalize text-nowrap'>Monthly Fee</p>
            <p>:</p>
            <p className='font-semibold flex items-center'>
              <TbCurrencyDollarCanadian />
              <span>{monthly_fee}</span>
            </p>
          </div>

          <div className='flex items-start gap-1 text-legal-dark-blue italic'>
            <p className='capitalize text-nowrap'>Additional Charge</p>
            <p>:</p>
            <p className='font-semibold flex items-center'>
              <TbCurrencyDollarCanadian />
              <span>{additional_hour_rate}/hr</span>
            </p>
          </div>

          <div className='flex items-start gap-1 text-legal-dark-blue italic'>
            <p className='capitalize text-nowrap'>Hours Included</p>
            <p>:</p>
            <p className='font-semibold'>{hours_included}</p>
          </div>

          <div className='flex items-start gap-1 text-legal-dark-blue italic'>
            <p className='capitalize text-nowrap'>Status</p>
            <p>:</p>
            <p
              className={`font-semibold ${
                is_active ? 'text-base-cyan' : 'text-base-red'
              }`}
            >
              {is_active ? 'Active' : 'Inactive'}
            </p>
          </div>
        </div>
      </AcademiaSectionWrapper>

      <AcademiaSectionWrapper
        sectionTitle='Agreement Document'
        additionalClassName='!mt-7 !px-4'
      >
        <div className='flex flex-col md:flex-row gap-3 items-center justify-between'>
          <p className='w-full md:flex-1 text-legal-dark-blue truncate'>
            {document.split('/')[3]}
          </p>

          <a
            href={`${process.env.REACT_APP_APPOINTMENT_SERVICE_PROD_API}${document}`}
            className='w-full md:w-fit flex items-center justify-center gap-1 bg-base-light-blue text-base-white text-sm px-4 py-1 rounded-md hover:opacity-70 active:scale-95 duration-300'
            target='_blank'
            rel='noreferrer'
          >
            <FaEye />
            <span>View Document</span>
          </a>
        </div>
      </AcademiaSectionWrapper>

      <AcademiaSectionWrapper
        sectionTitle='Agreement Notes'
        additionalClassName='!mt-7 !px-4'
      >
        <p className='text-legal-dark-blue'>{document_notes || 'N/A'}</p>
      </AcademiaSectionWrapper>
    </ParentModal>
  );
};

export default RetainAgreementDetailsModal;
